import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';

export enum DataSourceGroup {
  SCIENTIFIC_ARTICLE = 'Scientific articles',
  DATABASE = 'Databases',
  PUBLIC = 'Publicly available',
  CRITICAL_INFRASTRUCTURE = 'Governmental lists of critical infrastructure',
  COMPANY_REPORT = 'Company reports',
  REGULATORY = 'Regulations',
}

export type ImpactCategoryDataSource = {
  name: string;
  link?: string;
  group: DataSourceGroup;
  impactCategories: ImpactCategoryId[];
};

export const allImpactCategoryDataSources: ImpactCategoryDataSource[] = [
  {
    name: 'CORE database',
    link: 'https://core.ac.uk/',
    group: DataSourceGroup.SCIENTIFIC_ARTICLE,
    impactCategories: [
      ImpactCategoryId.SOCIETAL_INFRA,
      ImpactCategoryId.SOCIETAL_STABILITY,
      ImpactCategoryId.EQUALITY,
      ImpactCategoryId.KNOWLEDGE_INFRA,
      ImpactCategoryId.CREATES_KNOWLEDGE,
      ImpactCategoryId.DISTRIBUTING_KNOWLEDGE,
      ImpactCategoryId.PHYSICAL_DISEASES,
      ImpactCategoryId.MENTAL_DISEASES,
      ImpactCategoryId.RELATIONSHIPS,
      ImpactCategoryId.MEANING_AND_JOY,
      ImpactCategoryId.GHG_EMISSIONS,
      ImpactCategoryId.NON_GHG_EMISSIONS,
      ImpactCategoryId.SCARCE_NATURAL_RESOURCES,
      ImpactCategoryId.BIODIVERSITY,
      ImpactCategoryId.WASTE,
    ],
  },
  {
    name: 'OECD PIAAC database',
    link: 'https://www.oecd.org/skills/piaac/',
    group: DataSourceGroup.DATABASE,
    impactCategories: [ImpactCategoryId.CREATES_KNOWLEDGE, ImpactCategoryId.SCARCE_HUMAN_CAPITAL],
  },
  {
    name: 'OECD SDBS database',
    link: 'https://www.oecd.org/sdd/business-stats/structuralanddemographicbusinessstatisticssdbsoecd.htm',
    group: DataSourceGroup.DATABASE,
    impactCategories: [ImpactCategoryId.JOBS, ImpactCategoryId.TAXES],
  },
  {
    name: 'US',
    link: 'https://www.cisa.gov/critical-infrastructure-sectors',
    group: DataSourceGroup.CRITICAL_INFRASTRUCTURE,
    impactCategories: [ImpactCategoryId.SOCIETAL_INFRA],
  },
  {
    name: 'IPCC lifecycle CO2 equivalent for electricity production',
    link: 'https://www.ipcc.ch/site/assets/uploads/2018/02/ipcc_wg3_ar5_annex-ii.pdf#page=26',
    group: DataSourceGroup.DATABASE,
    impactCategories: [ImpactCategoryId.GHG_EMISSIONS],
  },
  {
    name: 'EU criticality levels of raw materials',
    link: 'https://single-market-economy.ec.europa.eu/sectors/raw-materials/areas-specific-interest/critical-raw-materials_en',
    group: DataSourceGroup.DATABASE,
    impactCategories: [ImpactCategoryId.SCARCE_NATURAL_RESOURCES],
  },
  {
    name: 'Our World In Data death rates from energy sources',
    link: 'https://ourworldindata.org/safest-sources-of-energy',
    group: DataSourceGroup.DATABASE,
    impactCategories: [ImpactCategoryId.PHYSICAL_DISEASES],
  },
  {
    name: 'Our World In Data environmental impacts of food',
    link: 'https://ourworldindata.org/environmental-impacts-of-food',
    group: DataSourceGroup.DATABASE,
    impactCategories: [ImpactCategoryId.GHG_EMISSIONS],
  },
  {
    name: 'UK GHG conversion factors',
    link: 'https://www.gov.uk/government/publications/greenhouse-gas-reporting-conversion-factors-2019',
    group: DataSourceGroup.DATABASE,
    impactCategories: [ImpactCategoryId.GHG_EMISSIONS],
  },
  {
    name: 'European commission GHG default emissions from biofuels',
    link: 'https://data.jrc.ec.europa.eu/dataset/jrc-alf-bio-biofuels_jrc_annexv_com2016-767_v1_july17',
    group: DataSourceGroup.DATABASE,
    impactCategories: [ImpactCategoryId.GHG_EMISSIONS],
  },
  {
    name: 'Global Burden of Diseases study',
    link: 'https://www.thelancet.com/gbd',
    group: DataSourceGroup.DATABASE,
    impactCategories: [ImpactCategoryId.PHYSICAL_DISEASES, ImpactCategoryId.MENTAL_DISEASES],
  },
  {
    name: 'CNF nutritional values',
    link: 'https://www.canada.ca/en/health-canada/services/food-nutrition/healthy-eating/nutrient-data/canadian-nutrient-file-2015-download-files.html',
    group: DataSourceGroup.DATABASE,
    impactCategories: [ImpactCategoryId.NUTRITION],
  },
  {
    name: 'WHO essential medicines',
    link: 'https://www.who.int/publications/i/item/WHO-MHP-HPS-EML-2021.02',
    group: DataSourceGroup.DATABASE,
    impactCategories: [ImpactCategoryId.PHYSICAL_DISEASES, ImpactCategoryId.MENTAL_DISEASES],
  },
  {
    name: 'EU conflict minerals regulation',
    link: 'https://www.who.int/publications/i/item/WHO-MHP-HPS-EML-2021.02',
    group: DataSourceGroup.REGULATORY,
    impactCategories: [ImpactCategoryId.SOCIETAL_STABILITY],
  },
  {
    name: 'Seafoodwatch',
    link: 'https://www.seafoodwatch.org',
    group: DataSourceGroup.DATABASE,
    impactCategories: [ImpactCategoryId.BIODIVERSITY],
  },
  {
    name: 'Product lifecycle assessments',
    link: 'https://www.iso.org/standard/37456.html',
    group: DataSourceGroup.PUBLIC,
    impactCategories: [
      ImpactCategoryId.GHG_EMISSIONS,
      ImpactCategoryId.NON_GHG_EMISSIONS,
      ImpactCategoryId.SCARCE_NATURAL_RESOURCES,
      ImpactCategoryId.BIODIVERSITY,
      ImpactCategoryId.WASTE,
    ],
  },
  {
    name: 'Environmental product declarations',
    link: 'https://www.iso.org/standard/38131.html',
    group: DataSourceGroup.PUBLIC,
    impactCategories: [
      ImpactCategoryId.GHG_EMISSIONS,
      ImpactCategoryId.NON_GHG_EMISSIONS,
      ImpactCategoryId.SCARCE_NATURAL_RESOURCES,
      ImpactCategoryId.BIODIVERSITY,
      ImpactCategoryId.WASTE,
    ],
  },
  {
    name: 'Our World In Data land use',
    link: 'https://ourworldindata.org/land-use',
    group: DataSourceGroup.DATABASE,
    impactCategories: [ImpactCategoryId.BIODIVERSITY],
  },
  {
    name: 'Company reports',
    group: DataSourceGroup.COMPANY_REPORT,
    impactCategories: [ImpactCategoryId.JOBS, ImpactCategoryId.TAXES, ImpactCategoryId.SCARCE_HUMAN_CAPITAL],
  },
];
